import React, { useEffect, useMemo, useState } from "react";

const n = (value) => Number(value ?? 0) || 0;

const fmt = (value) => n(value).toLocaleString("en-IN");

const dateLabel = (value) => value ? new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

const css = {

  panel: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12 },

  label: { fontSize: 12, color: "#475569" },

  number: { fontSize: 24, fontWeight: 800, marginTop: 4 },

  section: { fontSize: 14, fontWeight: 750, color: "#10234a", margin: "0 0 10px" },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, whiteSpace: "nowrap" },

  th: { padding: "9px 8px", background: "#eaf1fa", border: "1px solid #dbe3ef", textAlign: "right" },

  td: { padding: "8px", border: "1px solid #e2e8f0", textAlign: "right" },

};

const SOURCES = [

  ["social", "Social Campaign", "#e9f4ff", "#1266d6", "📣"],

  ["escort", "API'S", "#fff4dd", "#c57900", "👥"],

  ["supplied", "Supplied", "#e4f7ed", "#008956", "▤"],

  ["new_tractor", "New Tractor Enquiries", "#f0eaff", "#5426cc", "🚜"],

];

const STATE_COLS = ["social", "escort", "supplied", "web", "app", "fbe"];

const stateName = (s) => s.state_name ?? s.state ?? "Unknown";

const sourceValue = (s, key) => n(s[key] ?? 0);

const sum = (rows, key) => rows.reduce((acc, row) => acc + sourceValue(row, key), 0);

// Accept the nested Laravel API response as well as the original flat popup format.
const normalizeState = (state) => {
  const counts = state.lead_counts || {};
  const tractor = counts.new_tractor_enquiries || {};
  return {
    ...state,
    social: state.social ?? counts.social_generated ?? 0,
    escort: state.escort ?? counts.escort_generated ?? 0,
    supplied: state.supplied ?? counts.supplied_generated ?? 0,
    web: state.web ?? tractor.web ?? 0,
    app: state.app ?? tractor.app ?? 0,
    fbe: state.fbe ?? tractor.fbe ?? 0,
    generated: state.generated ?? counts.total_generated ?? 0,
    total: state.total ?? counts.total_generated ?? 0,
    remaining: state.remaining ?? state.achievement?.remaining_till_date ?? 0,
    achievement: typeof state.achievement === "object" ? state.achievement?.percentage ?? 0 : state.achievement,
  };
};

const normalizeReport = (brand) => {
  if (!brand) return null;
  const counts = brand.lead_counts || {};
  const tractor = counts.new_tractor_enquiries || {};
  return {
    ...brand,
    total_generated: brand.total_generated ?? counts.total_generated ?? 0,
    remaining_target: brand.remaining_target ?? brand.achievement?.remaining_till_date,
    achievement_percentage: brand.achievement_percentage ?? brand.achievement?.percentage,
    sources: brand.sources ?? {
      social: counts.social_generated ?? 0,
      escort: counts.escort_generated ?? 0,
      supplied: counts.supplied_generated ?? 0,
      new_tractor: tractor.total ?? 0,
      web: tractor.web ?? 0,
      app: tractor.app ?? 0,
      fbe: tractor.fbe ?? 0,
    },
    configured_states: (brand.configured_states ?? brand.state_breakdown ?? []).map(normalizeState),
    other_states: (brand.unwanted_states?.states ?? brand.other_states ?? []).map(normalizeState),
  };
};

function DataTable({ rows, configured, onViewAll }) {

  const [expanded, setExpanded] = useState(false);

  useEffect(() => setExpanded(false), [rows, configured]);

  const visible = expanded ? rows : rows.slice(0, 5);

  const cols = configured ? ["target", "till_date_target", ...STATE_COLS, "generated", "remaining", "achievement"] : [...STATE_COLS, "total"];

  const heading = { target: "Target", till_date_target: "Till Date Target", generated: "Generated", remaining: "Remaining", achievement: "Achievement", total: "Total", social: "Social", escort: "API'S", supplied: "Supplied", web: "Web", app: "App", fbe: "FBE" };

  const derived = (r, key) => {

    if (key === "generated" || key === "total") return r[key] == null ? STATE_COLS.reduce((a, c) => a + sourceValue(r, c), 0) : n(r[key]);

    if (key === "remaining") return r.remaining == null ? n(r.till_date_target) - derived(r, "generated") : n(r.remaining);

    if (key === "achievement") return r.achievement == null ? (n(r.till_date_target) ? derived(r, "generated") / n(r.till_date_target) * 100 : 0) : n(r.achievement);

    return sourceValue(r, key);

  };

  return (

    <div style={{ ...css.panel, background: configured ? "#f5fcf9" : "#fff6f6", marginTop: 10 }}>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>

        <h3 style={{ ...css.section, color: configured ? "#075b3c" : "#9c1111" }}>{configured ? "📍 State-wise Target Breakdown (Configured States)" : "❗ Unwanted / Other States (Non-configured States) — State-wise Details"}</h3>

        {rows.length > 5 && <button type="button" onClick={() => { setExpanded(!expanded); if (!expanded && onViewAll) onViewAll(); }} style={{ border: 0, background: "transparent", color: "#0866dc", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{expanded ? "Show Less" : "View All ↗"}</button>}

      </div>

      <div style={{ overflowX: "auto" }}>

        <table style={css.table}><thead><tr><th style={{ ...css.th, textAlign: "left" }}>#</th><th style={{ ...css.th, textAlign: "left" }}>State</th>{cols.map(c => <th key={c} style={css.th}>{heading[c]}</th>)}</tr></thead>

          <tbody>{visible.map((r, i) => <tr key={`${stateName(r)}-${i}`}><td style={{ ...css.td, textAlign: "left" }}>{i + 1}</td><td style={{ ...css.td, textAlign: "left" }}>{stateName(r)}</td>{cols.map(c => <td key={c} style={{ ...css.td, color: c === "remaining" && derived(r, c) < 0 ? "#dc2626" : c === "achievement" ? "#078647" : "inherit", fontWeight: ["total", "generated", "achievement"].includes(c) ? 700 : 400 }}>{c === "achievement" ? `${derived(r, c).toFixed(2)}%` : fmt(derived(r, c))}</td>)}</tr>)}

          {rows.length > 0 && <tr style={{ fontWeight: 800, background: "#eef2f8" }}><td style={css.td}></td><td style={{ ...css.td, textAlign: "left" }}>Total</td>{cols.map(c => <td key={c} style={css.td}>{c === "achievement" ? `${(sum(rows, "till_date_target") ? rows.reduce((a, r) => a + derived(r, "generated"), 0) / sum(rows, "till_date_target") * 100 : 0).toFixed(2)}%` : fmt(rows.reduce((a, r) => a + derived(r, c), 0))}</td>)}</tr>}

          {!rows.length && <tr><td colSpan={cols.length + 2} style={{ padding: 16, textAlign: "center", color: "#64748b" }}>No state records available</td></tr>}</tbody></table>

      </div>

    </div>

  );

}



/**

 * brands: [{id, title}] or [{brand_id, brand_name}]

 * fetchReport: async (brandId) => normalized report object

 * report shape: { brand_name, start_date, end_date, total_target, till_date_target,

 * total_generated, sources:{social,escort,supplied,new_tractor,web,app,fbe},

 * configured_states:[{state_name,target,till_date_target,social,escort,supplied,web,app,fbe,generated}],

 * other_states:[{state_name,social,escort,supplied,web,app,fbe,total}] }

 */

export default function BrandTargetReportPopup({ open, onClose, brands, fetchReport, reportData }) {

  const [selectedId, setSelectedId] = useState(null);

  const [search, setSearch] = useState("");

  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const apiBrands = useMemo(() => Array.isArray(reportData) ? reportData : (Array.isArray(reportData?.data) ? reportData.data : []), [reportData]);
  const normalized = useMemo(() => (apiBrands.length ? apiBrands : (brands || [])).map(b => ({ ...b, id: b.id ?? b.brand_id, name: b.title ?? b.brand_name ?? "Unnamed brand", total_generated: b.total_generated ?? b.lead_counts?.total_generated ?? 0 })), [brands, apiBrands]);

  useEffect(() => { if (open && normalized.length && !normalized.some(b => String(b.id) === String(selectedId))) setSelectedId(normalized[0].id); }, [open, normalized, selectedId]);

  useEffect(() => {

    if (!open || selectedId == null) return;

    let active = true;

    setLoading(true); setError(""); setReport(null);

    Promise.resolve().then(() => typeof fetchReport === "function" ? fetchReport(selectedId) : apiBrands.find(b => String(b.brand_id ?? b.id) === String(selectedId))).then(result => { if (active) setReport(normalizeReport(result?.data && !Array.isArray(result.data) ? result.data : result)); }).catch(err => { if (active) setError(err?.message || "Unable to load brand report"); }).finally(() => { if (active) setLoading(false); });

    return () => { active = false; };

  }, [open, selectedId, fetchReport, apiBrands]);

  useEffect(() => { if (!open) return; const key = e => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", key); return () => window.removeEventListener("keydown", key); }, [open, onClose]);

  if (!open) return null;

  const start = report?.start_date;

  const end = report?.end_date;

  const days = start && end ? Math.max(0, Math.round((new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000) + 1) : 0;

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const completed = report?.elapsed_days != null ? n(report.elapsed_days) : (start && end ? Math.max(0, Math.min(days, Math.floor((today - new Date(`${start}T00:00:00`)) / 86400000) + 1)) : 0);

  const left = Math.max(0, days - completed);

  const target = n(report?.total_target);

  const tillDate = n(report?.till_date_target);

  const generated = n(report?.total_generated);

  const remaining = Math.max(0, n(report?.remaining_target ?? (tillDate - generated)));

  const achievement = n(report?.achievement_percentage ?? (tillDate ? generated / tillDate * 100 : 0));

  const sources = report?.sources ?? {};

  const chosen = normalized.find(b => String(b.id) === String(selectedId));

  const summaryCards = [["Total Target", target, "#e6f3ff", "#10366c", "◎"], ["Till Date Target", tillDate, "#f0eaff", "#4e16bf", "▣"], ["Total Generated", generated, "#e5f8ef", "#07804d", "▥"], ["Remaining Till Date", remaining, "#ffebec", "#a50e19", "◷"]];

  return <div role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, background: "rgba(10,20,39,.66)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>

    <div role="dialog" aria-modal="true" aria-label="Brand Target Report" style={{ width: "min(1450px, 98vw)", maxHeight: "95vh", display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 13, background: "#f8fbff", color: "#10234a", boxShadow: "0 25px 80px rgba(0,0,0,.3)", fontFamily: "Arial, sans-serif" }}>

      <header style={{ padding: "16px 22px", borderBottom: "1px solid #dbe5f2", display: "flex", alignItems: "center", gap: 20 }}><h2 style={{ fontSize: 22, margin: 0 }}>Brand Target Report</h2><span style={{ fontSize: 13, color: "#526582" }}>{start && end ? `▣ ${dateLabel(start)} to ${dateLabel(end)}` : ""}</span><button aria-label="Close" onClick={onClose} style={{ marginLeft: "auto", background: "none", border: 0, fontSize: 26, color: "#243d63", cursor: "pointer" }}>×</button></header>

      <div style={{ display: "flex", overflow: "hidden", minHeight: 0, flex: 1 }}>

        <aside style={{ width: "min(310px, 30%)", flexShrink: 0, borderRight: "1px solid #dbe5f2", overflowY: "auto", padding: 14, background: "#fff" }}><input aria-label="Search brand" placeholder="⌕  Search brand..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: 11, border: "1px solid #d5e0ed", borderRadius: 6, marginBottom: 12 }} />{normalized.filter(b => b.name.toLowerCase().includes(search.toLowerCase())).map((b, i) => <button key={b.id} onClick={() => setSelectedId(b.id)} style={{ display: "flex", alignItems: "center", textAlign: "left", gap: 10, width: "100%", border: String(selectedId) === String(b.id) ? "1px solid #3186ff" : "1px solid #edf1f6", background: String(selectedId) === String(b.id) ? "#eaf3ff" : "white", borderRadius: 6, padding: "13px 8px", cursor: "pointer", marginBottom: 5, color: "#142c50" }}><span style={{ borderRadius: "50%", background: "#eaf0f8", padding: "6px 10px" }}>{i + 1}</span><span style={{ minWidth: 0 }}><strong>{b.name}</strong>{(b.total_target != null || b.total_generated != null) && <div style={{ fontSize: 11, color: "#526582", marginTop: 5 }}>Target: {fmt(b.total_target)} | Generated: {fmt(b.total_generated)}</div>}</span><span style={{ marginLeft: "auto" }}>›</span></button>)}{!normalized.length && <p>No brands available</p>}</aside>

        <main style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 16 }}>

          {loading ? <p>Loading brand target report...</p> : error ? <p role="alert" style={{ color: "#b91c1c" }}>{error}</p> : !report ? <p>Select a brand to view its report.</p> : <>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}><div><h2 style={{ margin: "0 0 4px", fontSize: 22 }}>{report.brand_name || chosen?.name}</h2><span style={css.label}>{dateLabel(start)} to {dateLabel(end)}</span></div><div style={{ ...css.panel, background: "#f2f6fc", display: "flex", gap: 18, fontSize: 12 }}>Total Days: <b>{days}</b> | Completed: <b>{completed}</b> | Days Left: <b style={{ color: "red" }}>{left}</b></div></div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(155px,1fr))", gap: 10 }}>{summaryCards.map(([label, value, bg, color, symbol]) => <div key={label} style={{ ...css.panel, background: bg, display: "flex", alignItems: "center", gap: 12 }}><span style={{ fontSize: 30, color }}>{symbol}</span><div><div style={{ ...css.label, color }}>{label}</div><div style={{ ...css.number, color }}>{fmt(value)}</div></div></div>)}</div>

            <div style={{ ...css.panel, marginTop: 10, background: "#f1f6fd" }}><div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}><strong style={{ fontSize: 13 }}>Target Achievement (Till Date)</strong><strong style={{ color: "#059669", fontSize: 21 }}>{achievement.toFixed(2)}%</strong></div><div style={{ background: "#dce5ef", height: 16, borderRadius: 10, overflow: "hidden", marginTop: 8 }}><div style={{ height: "100%", width: `${Math.min(100, Math.max(0, achievement))}%`, background: "#2eae88", borderRadius: 10 }} /></div></div>

            <div style={{ ...css.panel, marginTop: 10, background: "#f4f9ff" }}><h3 style={css.section}>▥ Lead Generation Summary (Source Wise)</h3><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>{SOURCES.map(([key, label, bg, color, icon]) => <div key={key} style={{ background: bg, borderRadius: 8, padding: 12, display: "flex", alignItems: "center", gap: 12 }}><span style={{ fontSize: 25 }}>{icon}</span><div><div style={{ fontSize: 12, color }}>{label}</div><strong style={{ fontSize: 22, color }}>{fmt(sources[key])}</strong></div></div>)}</div><div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 14, marginTop: 10, padding: 12, background: "#e7f2ff", borderRadius: 8, fontSize: 13 }}><strong style={{ color: "#0866dc" }}>New Tractor Enquiry Breakdown</strong>{[["Web", "web"], ["App", "app"], ["FBE", "fbe"]].map(([label, key]) => <span key={key}>{label}: <strong>{fmt(sources[key])}</strong></span>)}</div></div>

            <DataTable rows={report.configured_states ?? []} configured /><DataTable rows={report.other_states ?? []} configured={false} />

          </>}

        </main>

      </div>

    </div>

  </div>;

}
